import React, { useEffect } from "react";
import type { FiatPrice } from "@b2bportal/price-freeze-api";
import { trackEvent } from "@hopper-b2b/api";
import {
  type AirPriceFreezeMachineContext,
  AirPriceFreezeSelectors,
  useCheckoutState,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import {
  ConfirmationPage,
  PriceFreezeTrackingEvents,
} from "@hopper-b2b/fintech";

import { useI18nContext } from "@hopper-b2b/i18n";
import { useUberBridge } from "@hopper-b2b/utilities";
import config from "../../../../utils/config";
import type { TEvent } from "../../events";

import { Selectors } from "../../selectors";
import type { PassengerMap } from "../../types";

export const Confirmation = () => {
  const { t, formatFiatCurrency } = useI18nContext();
  const [state] = useCheckoutState<TEvent, AirPriceFreezeMachineContext>();
  const { setHeader } = useUberBridge();
  useEffect(() => {
    setHeader({
      title: t("priceFreezePurchase.documentTitle"),
    });
  }, [setHeader, t]);

  const finalizedPriceFreeze = useSelector(
    AirPriceFreezeSelectors.getFinalizedPriceFreeze
  );
  const isPageReady = !!finalizedPriceFreeze;

  const flightPricePerPax = useSelector(
    Selectors.getFinalizedFrozenFlightPricePerPax
  )?.fiat;

  const priceFreezeOfferPricePerPax = useSelector(
    Selectors.getFinalizedPriceFreezePerPax
  )?.fiat;

  const priceFreezeTotalAmount = useSelector(
    Selectors.getFinalizedPriceFreezeTotalAmount
  )?.fiat;

  const getPriceText = (
    fiat: FiatPrice | undefined,
    options?: Intl.NumberFormatOptions
  ) => (fiat ? formatFiatCurrency(fiat, options) : "");

  const flightPricePerPaxText = getPriceText(flightPricePerPax);

  //Special case remove decimals from PF price.
  const priceFreezeOfferPricePerPaxText = getPriceText(
    priceFreezeOfferPricePerPax,
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }
  );

  const priceFreezeTotalAmountText = getPriceText(priceFreezeTotalAmount);

  const expiresOnDateTime = useSelector(
    Selectors.getFinalizedPriceFreezeExpiration
  )!;

  const pricefreezeTtl = useSelector(Selectors.getFinalizedPriceFreezeTtl)!;

  const tripDetails = useSelector(Selectors.getTripDetails)!;

  const fareDetails = useSelector(Selectors.getFareDetails)!;

  const passengerMap = useSelector(Selectors.getFinalizedPassengerMap);

  const airports = useSelector(Selectors.getAirports);

  const onClick = () => {
    window.parent.postMessage(
      {
        type: "FINISH_SESSION",
        payload: null,
      },
      config.UBER_URL
    );
  };

  useEffect(() => {
    const priceFreezePurchaseProperties =
      Selectors.selectedPriceFreezePurchasePropertiesSelector(state);
    trackEvent({
      eventName: PriceFreezeTrackingEvents.SELECTED_PRICE_FREEZE_PURCHASE,
      ...priceFreezePurchaseProperties,
    });
  }, [isPageReady]);

  return (
    <ConfirmationPage
      flightPricePerPaxText={flightPricePerPaxText}
      priceFreezeOfferPricePerPaxText={priceFreezeOfferPricePerPaxText}
      expiresOnDateTime={expiresOnDateTime}
      tripDetails={tripDetails}
      fareDetails={fareDetails}
      handleViewPriceFreeze={onClick}
      priceFreezeTtl={pricefreezeTtl}
      priceFreezeTotalAmount={priceFreezeTotalAmountText}
      passengerMap={passengerMap as PassengerMap}
      airports={airports}
    />
  );
};
